import { _js } from '@ifixit/utils/localize';
import {
   DIALOG_BACKDROP,
   Dialog,
   DialogClose,
   DialogContentPrimitive,
   DialogPortal,
   DialogTitle,
} from '@ifixit/ui/components/dialog';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { createTrackedRoot } from 'Shared/react-mount-tracker';
// A guard, not a requirement: today's server-rendered pages happen to load
// the header's React entry, which imports this stylesheet already, but the
// lightbox shouldn't depend on that coincidence to render styled.
import 'Shared/tailwindcss/tailwind.css';

let launcherRoot: ReturnType<typeof createTrackedRoot> | null = null;

/**
 * Opens the lightbox from outside React, for the server-rendered pages
 * (guide view, Answers) whose click handlers live in plain JS. One root on
 * document.body serves every call; closing renders it empty rather than
 * unmounting so a later click reuses it.
 */
export function openImageLightbox(url: string, placeholder?: string) {
   const root = getLauncherRoot();
   root.render(
      <ImageLightbox url={url} placeholder={placeholder} onClose={() => root.render(null)} />
   );
}

function getLauncherRoot() {
   if (!launcherRoot) {
      const container = document.createElement('div');
      document.body.append(container);
      launcherRoot = createTrackedRoot(container);
   }
   return launcherRoot;
}

/**
 * The magnifying glass on a step image: opens it full frame in the browser
 * for inspecting details. Sits opposite the corner menu; a real overlay
 * button (not a menu entry) so it's one click away.
 */
export function MagnifyButton({ onClick }: { onClick: () => void }) {
   return (
      <button
         type="button"
         aria-label={_js('View full screen')}
         title={_js('View full screen')}
         // Stop the pointerdown from arming a drag on the tile.
         onPointerDown={event => event.stopPropagation()}
         onClick={onClick}
         // Touch devices have no hover to reveal the trigger, and 28px needs
         // the hit area grown past the icon rather than the icon itself.
         className="absolute left-1 top-1 flex size-7 items-center justify-center rounded-md bg-black/60 text-white opacity-0 outline-none transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-black/80 focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-white/80 pointer-coarse:opacity-100 pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']"
      >
         <FaIcon name="magnifying-glass-plus" className="size-4" />
      </button>
   );
}

type Box = { width: number; height: number };

/**
 * The box a guide image url's size name promises, like 800x600 for `.large`.
 * Originals end in `.full` and get none, so their standin can still shrink.
 */
function fileBox(url: string): Box | null {
   const name = new URL(url, window.location.href).pathname.split('.').pop() ?? '';
   const widths: Record<string, unknown> = App.imageSizeWidths ?? {};
   const heights: Record<string, unknown> = App.imageSizeHeights ?? {};
   const width = Object.hasOwn(widths, name) ? widths[name] : undefined;
   const height = Object.hasOwn(heights, name) ? heights[name] : undefined;
   return typeof width === 'number' && typeof height === 'number' ? { width, height } : null;
}

/**
 * Where the full file will land: the frame fit, never past the file's own
 * box, since a file smaller than the frame shows at its own size.
 */
function fitToFrame(
   picture: HTMLImageElement,
   frame: HTMLElement | null,
   fileSize: Box | null
): Box | null {
   if (!frame || !picture.naturalWidth || !picture.naturalHeight) {
      return null;
   }

   // The longhands, not padding-inline and padding-block: a shorthand
   // serializes as two lengths when the sides differ, and an engine that
   // does not resolve the logical one hands back an empty string.
   const padding = getComputedStyle(frame);
   const side = (name: string) => Number.parseFloat(padding.getPropertyValue(name)) || 0;
   const width = Math.min(
      frame.clientWidth - side('padding-left') - side('padding-right'),
      fileSize?.width ?? Infinity
   );
   const height = Math.min(
      frame.clientHeight - side('padding-top') - side('padding-bottom'),
      fileSize?.height ?? Infinity
   );
   const scale = Math.min(width / picture.naturalWidth, height / picture.naturalHeight);

   return {
      width: Math.round(picture.naturalWidth * scale),
      height: Math.round(picture.naturalHeight * scale),
   };
}

/**
 * The image full frame over the whole browser viewport, with a second size
 * a tap away.
 *
 * Fitting the image to the screen is the whole feature on a desktop, where
 * the page shows it in a column and the frame is far wider. On a phone the
 * page image is already full width, so a fitted view is the size it just
 * was: measured on an iPhone against a guide step, both 358px across. So
 * the image also opens to its own size, scrolled and pannable, which is
 * the part that actually magnifies.
 *
 * The backdrop closes, the image zooms. Tapping the picture used to close
 * too, which on a phone is most of the screen and the one place someone
 * reaches for to look closer.
 *
 * Zooming scrolls to the point that was tapped, so the enlarged view opens
 * on the detail someone pointed at rather than on the picture's top-left
 * corner. A keyboard press has no point, so it centers.
 */
export function ImageLightbox({
   url,
   srcSet,
   sizes,
   label,
   placeholder,
   onClose,
}: {
   url: string;
   srcSet?: string;
   sizes?: string;
   /** The dialog's accessible name; pass what the image is on this surface. */
   label?: string;
   /**
    * The rendition the page is already showing, to stand in until the full
    * one arrives. Pass what the reader clicked; anything else is a second
    * download rather than a cache hit.
    */
   placeholder?: string;
   onClose: () => void;
}) {
   const [zoomed, setZoomed] = useState(false);
   // Nothing to wait for without one, so the picture is its own first paint.
   const [loaded, setLoaded] = useState(!placeholder);
   // Where the full file will land. The standin is a smaller file of the
   // same picture, so at its own size it opens small and jumps when the
   // full one arrives. A picture fitted to the frame is decided by its
   // shape and the frame, and the two files share a shape, so the place
   // can be worked out before the full one exists.
   const [fitted, setFitted] = useState<Box | null>(null);
   const frameRef = useRef<HTMLDialogElement>(null);
   // Where in the picture the zoom was asked for, as a fraction of its box.
   const zoomOriginRef = useRef({ x: 0.5, y: 0.5 });

   /**
    * On the image's own load, not on the render that zoomed: the zoomed
    * picture is a replacement element (see the img below), so its size is
    * only known once the file behind it is.
    */
   function scrollToZoomOrigin() {
      const frame = frameRef.current;
      if (!frame || !zoomed) {
         return;
      }
      // The frame is a scroll container now, and a scroll container starts at
      // its top-left. Put the tapped point in the middle of it instead; the
      // browser clamps whatever falls outside.
      frame.scrollLeft = zoomOriginRef.current.x * frame.scrollWidth - frame.clientWidth / 2;
      frame.scrollTop = zoomOriginRef.current.y * frame.scrollHeight - frame.clientHeight / 2;
   }

   /**
    * The full file is fetched off to the side and the element keeps showing
    * the page's own rendition until it lands, so the frame opens on the
    * picture rather than on nothing. Shawn timed the wait at a second or
    * two, which is long enough to read as a dialog that failed to load.
    *
    * Off to the side rather than as the element's own src: swapping src in
    * place would blank the element while the new file downloads, which is
    * the gap this closes.
    *
    * A file that never arrives leaves the standin up. The reader keeps the
    * photo at the size the page had it, which beats the broken image they
    * would get from swapping to a url that just failed.
    */
   useEffect(() => {
      if (!placeholder) {
         return;
      }

      const full = new Image();
      const show = () => setLoaded(true);
      full.addEventListener('load', show);
      // Set before src, or the browser picks a source without them.
      if (srcSet) {
         full.srcset = srcSet;
      }
      if (sizes) {
         full.sizes = sizes;
      }
      full.src = url;

      // Closing mid-download otherwise pays for the whole file, which on the
      // slow connection this is for is the expensive case.
      return () => {
         full.removeEventListener('load', show);
         full.src = '';
         // srcset left set picks the same file again; removed before src,
         // WebKit starts a second download of src.
         full.removeAttribute('srcset');
      };
   }, [placeholder, url, srcSet, sizes]);

   function toggleZoom(event: MouseEvent<HTMLButtonElement>) {
      event.stopPropagation();
      const picture = event.currentTarget.getBoundingClientRect();
      // detail 0 is a keyboard activation, which has no coordinates.
      zoomOriginRef.current =
         event.detail === 0 || !picture.width || !picture.height
            ? { x: 0.5, y: 0.5 }
            : {
                 x: (event.clientX - picture.left) / picture.width,
                 y: (event.clientY - picture.top) / picture.height,
              };
      setZoomed(!zoomed);
   }

   return (
      <Dialog
         open
         onOpenChange={open => {
            if (!open) {
               onClose();
            }
         }}
      >
         <DialogPortal>
            <DialogContentPrimitive
               ref={frameRef}
               aria-describedby={undefined}
               tabIndex={-1}
               // The frame, not the picture. Left alone the dialog focuses
               // the first thing it can, which is the button around the
               // picture, and a browser draws that button's focus ring even
               // though the reader arrived by clicking: a white line around
               // the whole photo that reads as a border on it. The ring is
               // still there for anyone who tabs to the button.
               onOpenAutoFocus={event => {
                  event.preventDefault();
                  frameRef.current?.focus();
               }}
               // The backdrop, not the picture or the close button: see the note above.
               onClick={event => {
                  if (event.target === event.currentTarget) {
                     onClose();
                  }
               }}
               className={cn(
                  'data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-lightbox flex outline-none',
                  DIALOG_BACKDROP,
                  'backdrop:bg-black/90',
                  // Centering a scroll container with justify/align hides the
                  // overflow past its start edge; auto margins on the child
                  // center it and still leave the whole picture reachable.
                  zoomed
                     ? 'overflow-auto p-0'
                     : 'items-center justify-center p-4 pointer-coarse:p-0'
               )}
            >
               <DialogTitle className="sr-only">{label ?? _js('Image')}</DialogTitle>
               <button
                  type="button"
                  aria-label={zoomed ? _js('Zoom out') : _js('Zoom in')}
                  onClick={toggleZoom}
                  className={cn(
                     'outline-none focus-visible:ring-[3px] focus-visible:ring-white/80',
                     zoomed ? 'm-auto block cursor-zoom-out' : 'flex cursor-zoom-in'
                  )}
               >
                  <img
                     // A new element per size, not the same one restyled: with
                     // width descriptors the browser divides the file's pixels
                     // by the density `sizes` implies, so the image reports the
                     // size it was already fitted to and max-w-none has nothing
                     // left to release. Dropping the attributes in place does
                     // not undo that, because the source selection is skipped
                     // when it lands on the url the element already has. A
                     // replacement element reads the file at its own size,
                     // which the cache serves without another request.
                     key={loaded ? (zoomed ? 'zoomed' : 'fitted') : 'standin'}
                     // The standin holds through a zoom too. Swapping to a
                     // url still downloading would put the black frame back,
                     // on the one path a reader takes while they wait.
                     src={loaded ? url : placeholder}
                     srcSet={loaded && !zoomed ? srcSet : undefined}
                     sizes={loaded && !zoomed ? sizes : undefined}
                     onLoad={event => {
                        scrollToZoomOrigin();
                        if (!loaded) {
                           // With a srcset the full image takes its width from `sizes`,
                           // not from the file, so the file's box does not bound it.
                           const fileSize = srcSet ? null : fileBox(url);
                           setFitted(fitToFrame(event.currentTarget, frameRef.current, fileSize));
                        }
                     }}
                     // Given in pixels rather than left to the bounds: an
                     // image element fills the width and height it is given
                     // rather than fitting inside them, so a box worked out
                     // here is the only one that holds the picture's shape
                     // and stays the size of the picture. The button is the
                     // element's box, and a button wider than the picture
                     // zooms where the backdrop should close.
                     style={!loaded && fitted ? fitted : undefined}
                     alt=""
                     className={cn(
                        // Zoomed, the image takes its own width and the frame
                        // scrolls; max-w-none undoes the global img rule that
                        // would otherwise hold it to the viewport.
                        // Fitted, the bound is the viewport rather than the
                        // parent: a percentage would resolve against the button,
                        // whose height is its content's, leaving the button the
                        // full width of the frame with the picture letterboxed
                        // inside it. The backdrop beside the picture would then
                        // zoom instead of closing.
                        // On touch the picture runs edge to edge instead.
                        // The inset that lines the fitted view up with the
                        // page's own image is the widest a phone has to give,
                        // and the picture is what someone opened this for. The
                        // key is the pointer rather than the width: a phone
                        // turned sideways is 844px across, wider than sm.
                        // object-contain: the fitted box is set in pixels,
                        // so a bound clamping one axis would otherwise skew
                        // the picture rather than shrink it.
                        'object-contain',
                        zoomed
                           ? 'max-w-none'
                           : 'max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] pointer-coarse:max-h-[100dvh] pointer-coarse:max-w-[100vw]'
                     )}
                  />
               </button>
               <DialogClose
                  aria-label={_js('Close')}
                  // 44px on touch, where the guidance is a finger's width and
                  // this button sits at the edge of the screen. The safe-area
                  // tokens keep it 16px clear of the app's status bar and of a
                  // landscape notch.
                  className="fixed right-[calc(var(--safe-right)+--spacing(4))] top-[calc(var(--safe-top)+--spacing(4))] flex size-9 items-center justify-center rounded-md bg-black/50 text-white opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:size-11"
               >
                  <FaIcon name="xmark" className="size-5" />
               </DialogClose>
            </DialogContentPrimitive>
         </DialogPortal>
      </Dialog>
   );
}
